import { z } from 'zod';
/**
 * Organization-wide settings with per-location overrides — the shared vocabulary of the
 * two overridable groups, kiosk branding (`kiosk-branding.ts`) and kiosk settings
 * (`kiosk-setting.ts`).
 *
 * Each group lives in one table at two scopes: the ORGANIZATION row and, optionally, a
 * location's OVERRIDE row. Row presence decides override. Resolution is whole-record:
 *   1. the location row, when it exists AND the organization allows overrides;
 *   2. otherwise the organization row;
 *   3. otherwise the built-in defaults.
 * Fields are never mixed between the two rows.
 */
/** Which rung of the resolution produced the effective values. */
export const SETTINGS_SOURCES = ['LOCATION', 'TENANT', 'DEFAULT'];
export const settingsSourceSchema = z.enum(SETTINGS_SOURCES);
/**
 * A location that has its own override row. Listed on the organization DTO so the UI can
 * warn "N locations have custom settings that will stop applying" before the organization
 * turns `allowLocationOverride` off (which PAUSES overrides: the rows are kept, and ignored).
 */
export const customizedLocationSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
});
/** The organization-row fields both groups share. */
export const organizationOverridePolicySchema = z.object({
    /** Whether location overrides apply. Off = every location uses the organization values. */
    allowLocationOverride: z.boolean(),
    /** Locations (visible to the caller) that have their own override row, ordered by name. */
    customizedLocations: z.array(customizedLocationSchema),
});
export function locationSettingsViewSchema(values) {
    return z.object({
        locationId: z.string().min(1),
        effective: values,
        source: settingsSourceSchema,
        organization: values,
        override: values.nullable(),
        overrideAllowed: z.boolean(),
    });
}
/** The 409 `error` code a location-row write answers while the organization disallows overrides. */
export const LOCATION_OVERRIDE_NOT_ALLOWED = 'LOCATION_OVERRIDE_NOT_ALLOWED';
//# sourceMappingURL=overridable-settings.js.map